import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ICompetitorPriceData,
  ISecondaryMarketplaceMasterData,
} from 'models/interfaces';
import { CompetitorPricesService as SkyBoxCompetitorPricesService } from 'services/SkyboxServices';
import { CompetitorPricesService as StubHubCompetitorPricesService } from 'services/StubHubServices';
import { LoggerService } from 'sharedServices';

interface ICompetitorPricesService {
  getCompetitorPrices(args: any): Promise<any>;
  getSecondaryMarketplaceMaster(): Promise<any>;
}

let initialState: {
  secondaryMarketplaceMaster: ISecondaryMarketplaceMasterData | null;
  competitorPrices: ICompetitorPriceData[];
  bulkPlacementCurrentSelectedMarketplaceNameAlias: string | null;
} = {
  secondaryMarketplaceMaster: null,
  competitorPrices: [],
  bulkPlacementCurrentSelectedMarketplaceNameAlias: null,
};

class CompetitorPricesServiceFactory {
  private static skyBoxService: ICompetitorPricesService;
  private static stubHubService: ICompetitorPricesService;

  static getService(
    environment: 'stubHub' | 'skyBox',
  ): ICompetitorPricesService {
    if (environment === 'stubHub') {
      if (!this.stubHubService) {
        this.stubHubService = new StubHubCompetitorPricesService();
      }
      return this.stubHubService;
    } else {
      if (!this.skyBoxService) {
        this.skyBoxService = new SkyBoxCompetitorPricesService();
      }
      return this.skyBoxService;
    }
  }
}

const getCompetitorPrices = createAsyncThunk(
  'competitorPrices/getCompetitorPrices',
  async (
    args: {
      event_id: number;
      venue_id: number;
      site_type: string;
      seatmap_url: string;
      force_refresh: boolean;
      functionName: string;
      env: 'skyBox' | 'stubHub';
    },
    { rejectWithValue },
  ) => {
    try {
      let response = await CompetitorPricesServiceFactory.getService(
        args.env,
      ).getCompetitorPrices({
        event_id: args.event_id,
        venue_id: args.venue_id,
        site_type: args.site_type,
        seatmap_url: args.seatmap_url,
        force_refresh: args.force_refresh,
      });
      let responseData = response.data.data;
      return responseData;
    } catch (error: any) {
      new LoggerService().log({
        payload: error,
        function_name: 'getCompetitorPrices',
      });
      return rejectWithValue({
        code: error.code,
        message: error.message,
        refreshAction: args.functionName,
      });
    }
  },
);

const getSecondaryMarketplaceMaster = createAsyncThunk(
  'competitorPrices/getSecondaryMarketplaceMaster',
  async (
    args: {
      functionName: string;
      env: 'skyBox' | 'stubHub';
    },
    { rejectWithValue },
  ) => {
    try {
      let response = await CompetitorPricesServiceFactory.getService(
        args.env,
      ).getSecondaryMarketplaceMaster();
      let responseData = response.data.data;
      return responseData;
    } catch (error: any) {
      await new LoggerService().log({
        payload: error,
        function_name: 'getSecondaryMarketplaceMaster',
      });
      return rejectWithValue({
        code: error.code,
        message: error.message,
        refreshAction: args.functionName,
      });
    }
  },
);

export const CompetitorPricesSlice = createSlice({
  name: 'CompetitorPrices',
  initialState,
  reducers: {
    resetCompetitorPricesSlice: () => initialState,
    setBulkPlacementCurrentSelectedMarketplaceNameAlias: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.bulkPlacementCurrentSelectedMarketplaceNameAlias = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCompetitorPrices.pending, (state, action: any) => {
      const _competitorPrices = [...state.competitorPrices];
      const _findCompetitorPrice = _competitorPrices.find(
        (each) =>
          each.event_id === action.meta.arg.event_id &&
          each.provider.toLowerCase() ===
            action.meta.arg.site_type.toLowerCase(),
      );
      if (_findCompetitorPrice) {
        _findCompetitorPrice.isLoading = true;
      } else {
        _competitorPrices.push({
          event_id: action.meta.arg.event_id,
          provider: action.meta.arg.site_type.toLowerCase(),
          isLoading: true,
          hasError: false,
          dataFetchError: null,
          metadata: [],
          updated_on: null,
          msg_id: null,
          messege: null,
        });
      }
      state.competitorPrices = _competitorPrices;
    });
    builder.addCase(getCompetitorPrices.fulfilled, (state, action: any) => {
      const _competitorPrices = [...state.competitorPrices];
      const _findCompetitorPrice = _competitorPrices.find(
        (each) =>
          each.event_id === action.meta.arg.event_id &&
          each.provider.toLowerCase() ===
            action.meta.arg.site_type.toLowerCase(),
      );
      if (_findCompetitorPrice) {
        _findCompetitorPrice.isLoading = false;
        _findCompetitorPrice.dataFetchError = null;
        _findCompetitorPrice.metadata = action.payload.metadata ?? [];
        _findCompetitorPrice.updated_on = action.payload.updated_on ?? null;
        _findCompetitorPrice.hasError =
          action.payload.msg_id === 1 ? false : true;
        _findCompetitorPrice.msg_id = action.payload.msg_id;
        _findCompetitorPrice.messege = action.payload.messege
          ? action.payload.messege
          : 'Something Went Wrong';
      } else {
        _competitorPrices.push({
          event_id: action.meta.arg.event_id,
          provider: action.meta.arg.site_type.toLowerCase(),
          isLoading: false,
          dataFetchError: null,
          metadata: action.payload.metadata ?? [],
          updated_on: action.payload.updated_on ?? null,
          hasError: action.payload.msg_id === 1 ? false : true,
          msg_id: action.payload.msg_id,
          messege: action.payload.messege
            ? action.payload.messege
            : 'Something Went Wrong',
        });
      }
    });
    builder.addCase(getCompetitorPrices.rejected, (state, action: any) => {
      const _competitorPrices = [...state.competitorPrices];
      const _findCompetitorPrice = _competitorPrices.find(
        (each) =>
          each.event_id === action.meta.arg.event_id &&
          each.provider.toLowerCase() ===
            action.meta.arg.site_type.toLowerCase(),
      );
      if (_findCompetitorPrice) {
        _findCompetitorPrice.isLoading = false;
        _findCompetitorPrice.hasError = true;
        _findCompetitorPrice.messege = 'Something Went Wrong';
        _findCompetitorPrice.dataFetchError = { ...action.payload };
      } else {
        _competitorPrices.push({
          event_id: action.meta.arg.event_id,
          provider: action.meta.arg.site_type.toLowerCase(),
          isLoading: false,
          hasError: true,
          dataFetchError: { ...action.payload },
          metadata: [],
          msg_id: null,
          messege: 'Something Went Wrong',
          updated_on: null,
        });
      }
    });

    builder.addCase(
      getSecondaryMarketplaceMaster.fulfilled,
      (state, action: any) => {
        state.secondaryMarketplaceMaster = { ...action.payload };
      },
    );
  },
});

// const competitorPricesList = (state: RootState) => state.competitorPrices.competitorPrices;
// const secondaryMarketplaceMaster = (state: RootState) => state.competitorPrices.secondaryMarketplaceMaster;
// const bulkPlacementCurrentSelectedMarketplaceNameAlias = (state: RootState) => state.competitorPrices.bulkPlacementCurrentSelectedMarketplaceNameAlias;

// export {
//   competitorPricesList,
//   secondaryMarketplaceMaster,
//   bulkPlacementCurrentSelectedMarketplaceNameAlias,
// };
export const competitorPricesActions = {
  getSecondaryMarketplaceMaster,
  getCompetitorPrices,
};
export const {
  setBulkPlacementCurrentSelectedMarketplaceNameAlias,
  resetCompetitorPricesSlice,
} = CompetitorPricesSlice.actions;
export default CompetitorPricesSlice.reducer;
